import * as _ from "lodash";
import * as React from "react";
import { useMappedState } from "redux-react-hook";
import styled, { css } from "styled-components";
import * as queryString from "query-string";
import { useState } from "react";

import {
  FilterParams,
  PaginatedParams
} from "src/library/utils";
import { media } from "src/library/utils/styled-media-queries";
import { ITableInstance } from "src/library/components/Tables/Table";
import Grid from "src/library/components/Hedron";
import ProductSearchFilter from "src/core/products/components/ProductSearchFilter";
import ProductCategoryFilter from "src/core/products/components/ProductCategoryFilter";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import ProductTagFilters from "src/modules/product-tags/components/ProductTagFilters";
import { breakpoint } from "src/library/utils/styled-media-queries/index";
import {
  IProduct,
  ListProductsFilters,
  useProductsApi
} from "src/core/products/api/products.api";
import type { IStore } from "src/core/shared/store";
import { useDrop } from "src/core/drops/hooks";
import useProductSearchFilters from "src/core/products/hooks/useProductSearchFilters";
import { PageProps } from "src/pages/Router/routes";
import {
  filterProductSubstitutions,
  IProductMaybeWithSubstitution
} from "src/core/products/utils";
import { ProductCard } from "src/core/products/components/ProductCard";
import { productToGTMItem } from "src/core/products/utils/product-to-gtm-item";
import { useGTMEvent } from "src/core/shared/hooks/useGTMEvent";
import PromotionalTagFilter from "src/core/products/components/PromotionalTagFilter";
import FeaturedProductsBanner from "src/core/products/components/FeaturedProductsBanner";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";
import useLocalStorage from "src/library/hooks/useLocalStorage";
import { Text } from "src/library/components/Text";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { InfinitLoadMore } from "src/core/shared/components/InfiniteLoadMore";

const pageSize = 12;

/**
 * Main "Products" View
 *
 * @returns
 */
const ProductsPage: React.ReactFCWithChildren<PageProps<unknown>> = (props => {
  const TableRef = React.useRef<ITableInstance<IProduct>>(null);
  const [ { drop, dropIdMemo } ] = useDrop();
  const gtmEvent = useGTMEvent();

  const {
    orderDeliveryDate, installedModules, displaySettings, currentRole, currentList
  } = useMappedState(({
    orders, global, lists
  }: IStore) => ({
    currentOrder: orders.currentOrder,
    orderDeliveryDate: orders.currentOrder?.data?.orderDeliveryDate,
    installedModules: global.settings ? global.settings.installedModules || [] : [],
    displaySettings: global.settings?.displaySettings,
    currentList: lists.currentList,
    currentRole: global.role

  }));

  const [ tableView, setTableView ] = useLocalStorage<"grid" | "compact">("products-table-view", "grid");
  const productSearchFilters = useProductSearchFilters();
  const [ listProductsResponse, listProducts ] = useProductsApi("LIST_PRODUCTS");
  const [ products, setProducts ] = useState<IProductMaybeWithSubstitution[]>([]);
  // used to prevent the event being fired too often
  const [ gtmEventFired, setGtmEventFired ] = React.useState(false);
  const [ featuredProducts, setFeaturedProducts ] = React.useState<IProduct[]>([]);
  const [ disbaleLoadMore, setDisableLoadMore ] = React.useState(false);

  const [ pagination, setPagination ] = useState<PaginatedParams<IProduct>>({
    page: 0,
    limit: pageSize,
    offset: 0,
    sort: [ [ displaySettings?.product.defaultSort || "partNumber", "ASC" ] ]
  });

  // filter out substituted products and nest them inside replacement products
  React.useEffect(() => {
    if (listProductsResponse.data) {
      const productsWithSubs = filterProductSubstitutions(listProductsResponse.data);

      setGtmEventFired(false);

      setProducts(prev => {
        setDisableLoadMore(false);

        return _.uniqBy([ ...prev, ...productsWithSubs ], "id");
      }
      );
    }
  }, [
    listProductsResponse.data,
    currentList.data,
    currentList.mode,
    currentRole?.code
  ]);

  const [ pageFilters, setPageFilters ] = React.useState<ListProductsFilters | null>(null);
  const pageFiltersMemo = React.useMemo(() => pageFilters, [ pageFilters ]);
  const [ currentCategory, setCurrentCategory ] = React.useState<string>();

  const loadPageFilters = React.useCallback(() => {
    const newFilters = productSearchFilters();

    if (props.location.search) {
      const params = queryString.parse(props.location.search);

      if (params.category) {
        newFilters.categories = { id: parseInt(params.category as string, 10) };
      }
    }

    setPageFilters(_.omitBy(newFilters, _.isNull));
  }, [ productSearchFilters, props.location.search ]);

  React.useEffect(() => {
    if (dropIdMemo || currentRole?.code === "customer-admin") {
      loadPageFilters();
    }
  }, [
    orderDeliveryDate,
    dropIdMemo,
    currentRole?.code,
    loadPageFilters
  ]);

  const onFetchData = React.useCallback(
    (pagination: PaginatedParams<IProduct>, filters: ListProductsFilters | null) => {
      if (currentRole?.code !== "customer-admin" && !filters?.priceCodeId) {
        return;
      }

      listProducts({
        pagination,
        filters: filters || {}
      });
    },
    [ currentRole?.code, listProducts ]
  );

  React.useEffect(() => {
    if (pageFiltersMemo) {
      onFetchData(pagination, pageFiltersMemo);
    }
  }, [
    pagination,
    pageFiltersMemo,
    onFetchData
  ]);

  React.useEffect(() => {
    if (listProductsResponse.data) {
      if (listProductsResponse.data.count < pageSize) {
        setFeaturedProducts([]);
      } else if (listProductsResponse.data?.featuredProducts) {
        setFeaturedProducts(listProductsResponse.data?.featuredProducts);
      }
    }
  }, [ listProductsResponse ]);

  /**
   * Handle change to a filter
   */
  const handleFilterChange = React.useCallback(
    (newFilters: FilterParams<IProduct>) => {
      setProducts([]);

      setPageFilters({
        ...pageFilters,
        ...newFilters
      });

      setPagination(currPagination => ({
        ...currPagination,
        page: 0,
        offset: 0
      }));
    },
    [ pageFilters ]
  );

  React.useEffect(() => {
    if (!gtmEventFired && listProductsResponse.data && TableRef.current) {
      const page = TableRef.current?.props?.page || 0;
      const pageSize = TableRef.current?.props?.pageSize || 0;

      gtmEvent("view_item_list", {
        ecommerce: {
          item_list_name: currentCategory,
          items: _.map(listProductsResponse.data.result, (product, index) => productToGTMItem(product, { index: index + (page > 0 ? (page * pageSize) : 0) }))
        }
      });
      setGtmEventFired(true);
    }
  }, [
    listProductsResponse.data,
    currentCategory,
    gtmEventFired,
    gtmEvent
  ]);

  return (
    <Layout
      {...props}
      $noPadding
    >
      <ProductPageContent fill>
        <div className="filters-wrapper">
          {drop && (
            <TableFiltersWrapper>
              <ProductSearchFilter handleSearch={handleFilterChange} />

              <ProductCategoryFilter
                handleSearch={categoryDetails => {
                  if (categoryDetails.categories.name) {
                    setCurrentCategory(categoryDetails.categories.name);
                  } else {
                    setCurrentCategory(undefined);
                  }

                  handleFilterChange({ categories: categoryDetails.categories });
                }}
              />

              {installedModules.includes("product-tag") && <ProductTagFilters handleSearch={handleFilterChange} />}
            </TableFiltersWrapper>
          )}

          <PromotionalTagFilter handleSearch={handleFilterChange} />
        </div>

        {!!featuredProducts.length && <FeaturedProductsBanner featuredProducts={featuredProducts} />}

        <Grid.Bounds className="product-list">

          <Grid.Box width="100%">
            {listProductsResponse.data && (
              <div className="toggle-product-view-wrapper">
                <div
                  className="toggle-product-view"
                  onClick={() => setTableView(tableView === "compact" ? "grid" : "compact")}
                >
                  <Text>
                    Switch to
                    {" "}

                    {tableView === "grid" ? "list" : "grid"}

                    {" "}
                    view
                  </Text>

                  {tableView === "compact" ? <Icon icon={Icons.GRID} /> : <Icon icon={Icons.TABLE} />}
                </div>
              </div>
            )}

            <div className={`products-list ${tableView}`}>
              {_.map(products, prod => (
                <div
                  className="product-card-wrapper"
                  key={`product-${prod.id}`}
                >
                  <ProductCard
                    productImage={prod.image || ""}
                    product={prod}
                    substitution={prod.substitution}
                    drop={drop}
                    view={tableView}
                  />
                </div>
              ))}
            </div>

            {listProductsResponse.pending && (
              <LoadingSpinner
                type="flat"
                position="inline"
              />
            )}

            <InfinitLoadMore
              disabled={disbaleLoadMore || !products.length || listProductsResponse.pending || !listProductsResponse.data || pagination.offset + pageSize >= listProductsResponse.data?.count}
              onLoad={() => {
                if (!disbaleLoadMore && listProductsResponse.data?.count && (pagination.offset + pageSize) < listProductsResponse.data?.count) {
                  setDisableLoadMore(true);

                  setPagination(prevPagination => ({
                    ...prevPagination,
                    page: prevPagination.page + 1,
                    offset: prevPagination.offset + pageSize
                  }));
                }
              }}
            />

          </Grid.Box>
        </Grid.Bounds>
      </ProductPageContent>

    </Layout>
  );
});

const ProductPageContent = styled(MainContentWrapper)`
  ${({ theme }) => css`
    .filters-wrapper, .product-list {
      padding-left: ${theme.spacing.padding.sm};
      padding-right: ${theme.spacing.padding.sm};


      ${breakpoint("md", theme)} {
        padding-left: ${theme.spacing.padding.lg};
        padding-right: ${theme.spacing.padding.lg};
      }
    }
    .products-list {
      margin-top: ${theme.spacing.margins.md};
    }

    .products-list.grid {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;

      .product-card-wrapper {
        background: ${theme.colors.white};
        box-shadow: ${theme.shadows.default};
        width: 100%;
        max-width: 400px;
        margin-bottom: ${theme.spacing.margins.md};

        > div {
          margin: auto;
        }

        .pack-size-selector-wrapper, .order-control-wrapper, .quantity-controls {
          display: flex;
          justify-content: flex-end;
        }

        .pack-quantity-label span {
          text-align: end;
        }
      }

      ${breakpoint("lg", theme)} {
        justify-content: flex-start;

        .product-card-wrapper {
          width: 48%;
          
          &:nth-child(odd) {
            margin-right: 2%;
          }
        }
      }

      ${breakpoint("xl", theme)} {
        .product-card-wrapper {
          width: 31%;
          margin-right: calc(4.666% / 2);

          &:nth-child(odd) {
            margin-right: calc(4.666% / 2);
          }
          
          &:nth-child(3n) {
            margin-right: 0;
          }
        }
      }

      ${breakpoint("xxl", theme)} {
        .product-card-wrapper {
          width: 23%;
          margin-right: calc(6% / 3);

          &:nth-child(odd) {
            margin-right: calc(6% / 3);
          }
          
          &:nth-child(3n) {
            margin-right: calc(6% / 3);
          }
        }
      }
    }
    
    .products-list.compact {
      .product-card-wrapper {
        margin-bottom: ${theme.spacing.margins.md};
      }
    }

    .toggle-product-view-wrapper {
      margin-top: ${theme.spacing.margins.sm};
      margin-right: ${theme.spacing.margins.md};
      display: flex;
      justify-content: flex-end;

      .toggle-product-view {
        display: flex;
        align-items: center;

        svg {
          margin-left: ${theme.spacing.margins.sm};
        }

        &:hover {
          cursor: pointer;
        }
      }
    }
  `}
`;

const TableFiltersWrapper = styled(Grid.Bounds)`
  ${({ theme }) => css`
    background: ${theme.colors.white};
    border-radius: ${theme.borderRadius};
    padding: ${theme.spacing.padding.md};
    flex-direction: column;
    padding-bottom: 0;

    > div {
      margin-bottom: ${theme.spacing.margins.sm};
      margin-right: ${theme.spacing.margins.sm};

      .dropdown-select, .search-input {
        width: 100%;
      }

   
    }

    ${media({ gt: theme.breakpoints.sm })} {
      flex-direction: row;
      flex-wrap: wrap;

      > div {
        margin-bottom: ${theme.spacing.margins.md};

        .dropdown-select, .search-input {
          width: 200px;
        }
      }
    }
  `};
`;

export default ProductsPage;
